import React from 'react';
import {IconButton} from '@wix/wix-ui-tpa/cssVars';
import {ChevronLeft, ChevronRight} from '@wix/wix-ui-icons-common/on-stage';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import {useCarouselScrollState} from './useCarouselScrollState';
import {classes} from './Carousel.st.css';

export enum CarouselDataHook {
  root = 'Carousel.root',
  scroller = 'Carousel.scroller',
  item = 'Carousel.item',
  previousArrow = 'Carousel.previousArrow',
  nextArrow = 'Carousel.nextArrow',
}

export const ITEM_GAP = 20;

export interface CarouselProps {
  title: React.ReactNode;
  items: {id: string; content: React.ReactNode}[];
  itemWidth: number;
  previousArrowAriaLabel: string;
  nextArrowAriaLabel: string;
}

export const Carousel = ({title, items, itemWidth, previousArrowAriaLabel, nextArrowAriaLabel}: CarouselProps) => {
  const {isRTL} = useEnvironment();
  const scrollDirection = isRTL ? -1 : 1;
  const backIcon = isRTL ? <ChevronRight /> : <ChevronLeft />;
  const forwardIcon = isRTL ? <ChevronLeft /> : <ChevronRight />;
  const {scroller, canScrollToPrevious, canScrollToNext, syncScrollState} = useCarouselScrollState();

  const scrollByItems = (count: number) => {
    scroller.current.scrollLeft += count * scrollDirection * (itemWidth + ITEM_GAP);
    syncScrollState();
  };

  return (
    <div data-hook={CarouselDataHook.root} className={classes.root}>
      <div className={classes.header}>{title}</div>
      <div className={classes.scrollArea}>
        <IconButton
          data-hook={CarouselDataHook.previousArrow}
          className={classes.arrow}
          style={{height: itemWidth}}
          icon={backIcon}
          aria-label={previousArrowAriaLabel}
          disabled={!canScrollToPrevious}
          onClick={() => scrollByItems(-1)}
        />
        <ul
          ref={scroller}
          data-hook={CarouselDataHook.scroller}
          className={classes.scroller}
          style={{gap: ITEM_GAP}}
          onScroll={syncScrollState}>
          {items.map((item) => (
            <li key={item.id} data-hook={CarouselDataHook.item} style={{width: itemWidth}}>
              {item.content}
            </li>
          ))}
        </ul>
        <IconButton
          data-hook={CarouselDataHook.nextArrow}
          className={classes.arrow}
          style={{height: itemWidth}}
          icon={forwardIcon}
          aria-label={nextArrowAriaLabel}
          disabled={!canScrollToNext}
          onClick={() => scrollByItems(1)}
        />
      </div>
    </div>
  );
};
